import * as React from 'react';
import {createViewConfig} from '@sail/engine';
import {useUpdateEffect} from '@sail/react';
import {view} from '../../view';
import type {CodePuncherProps} from './types';
import {useInputSelection} from './hooks';
import type {CodePuncherDigitData} from './CodePuncherDigit';
import {CodePuncherDigit, CodePuncherDigitConfig} from './CodePuncherDigit';

import {CodePuncherInput} from './CodePuncherInput';
import type {FormFieldProps} from '../FormField';
import {FormField, useFormFieldProps} from '../FormField';

export const NativeCodePuncherConfig = createViewConfig({
  props: {} as CodePuncherProps,
  name: 'NativeCodePuncher',
  defaults: {
    value: '',
    disabled: false,
  },
});

const NativeCodePuncher = NativeCodePuncherConfig.createView(
  ({
    render,
    onClick,
    onChange,
    disabled,
    onComplete,
    value = '',
    maxLength = 6,
    autoFocus = false,
    noAutofill = false,
    subviews,
    ...props
  }) => {
    const [internalValue, setInternalValue] = React.useState(value);

    const inputRef = React.useRef<HTMLInputElement>(null);

    const selection = useInputSelection(inputRef);

    // Sync external value changes
    useUpdateEffect(() => {
      if (value !== internalValue) {
        setInternalValue(value);

        if (inputRef.current?.ownerDocument.activeElement === inputRef.current) {
          // Dispatch event to recalculate selection due to external change
          inputRef.current?.dispatchEvent(new Event('selectionchange'));
        }
      }
    }, [value]);

    React.useLayoutEffect(() => {
      if (internalValue.length !== maxLength) {
        return;
      }

      const animationFrameId = requestAnimationFrame(() => {
        onComplete?.(internalValue);
      });

      return () => {
        cancelAnimationFrame(animationFrameId);
      };
    }, [internalValue, maxLength, onComplete]);

    // Handle input changes - update internal state and call external callback
    const handleOnChange = React.useCallback(
      (newValue: string) => {
        setInternalValue(newValue);
        onChange?.(newValue);
      },
      [onChange],
    );

    const handleOnClick = React.useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        if (disabled) {
          return;
        }

        // eslint-disable-next-line @sail/ui-no-reflow
        inputRef.current?.focus();

        onClick?.(event);
      },
      [onClick, disabled],
    );

    const digits = React.useMemo(
      () =>
        Array.from({length: maxLength}).reduce<Array<CodePuncherDigitData>>(
          (acc, _, idx) => {
            const digitValue = internalValue[idx] || '';

            const isActive =
              !disabled &&
              !!selection &&
              selection.end >= idx &&
              selection.start <= idx;
          
            return [
              ...acc,
              {
                disabled,
                active: isActive,
                value: digitValue,
              },
            ];
          },
          [],
        ),
      [maxLength, internalValue, selection, disabled],
    );

    const content = React.useMemo(() => {
      if (render) {
        return render(digits);
      }

      const groupSize = Math.floor(maxLength / 2);
      const firstGroup = digits.slice(0, groupSize);
      const secondGroup = digits.slice(groupSize);

      return (
        <React.Fragment>
          <view.div inherits={subviews.digitGroup}>
            {firstGroup.map((slot, index) => (
              <CodePuncherDigit
                key={index}
                inherits={subviews.digit}
                {...slot}
              />
            ))}
          </view.div>

          <view.div inherits={subviews.separator} />

          <view.div inherits={subviews.digitGroup}>
            {secondGroup.map((slot, index) => (
              <CodePuncherDigit
                key={index}
                inherits={subviews.digit}
                {...slot}
              />
            ))}
          </view.div>
        </React.Fragment>
      );
    }, [
      maxLength,
      digits,
      render,
      subviews.digitGroup,
      subviews.separator,
      subviews.digit,
    ]);

    return (
      <view.div {...props} onClick={handleOnClick}>
        {content}

        <view.div
          css={{
            inset: 'space.0',
            position: 'absolute',
            pointerEvents: 'none',
          }}
        >
          <CodePuncherInput
            ref={inputRef}
            disabled={disabled}
            value={internalValue}
            maxLength={maxLength}
            autoFocus={autoFocus}
            noAutofill={noAutofill}
            onChange={handleOnChange}
          />
        </view.div>
      </view.div>
    );
  },
  {
    css: {
      stack: 'x',
      gap: 'small',
      width: 'fit',
      position: 'relative',
      alignItems: 'center',
    },
    variants: {
      error: {
        true: {
          uses: [
            CodePuncherDigitConfig.customize({
              css: {
                keyline: 'form.error.disabled',
              },
            }),
          ],
        },
      },
    },
    subviews: {
      digitGroup: {
        css: {
          stack: 'x',
          alignItems: 'center',
        },
      },
      separator: {
        css: {
          width: 'small',
          height: 'xxsmall',
          borderRadius: 'xsmall',
          backgroundColor: 'form.accent.disabled',
        },
      },
    },
  },
);

export const CodePuncherConfig = createViewConfig({
  name: 'CodePuncher',
  props: {} as FormFieldProps<CodePuncherProps, typeof NativeCodePuncher>,
});

/**
 * CodePuncher is a specialized input component for entering verification codes,
 * typically 6-digit codes sent via SMS or email. It provides individual visual boxes
 * for each digit with a single underlying input for better accessibility and simpler state management.
 */
export const CodePuncher = CodePuncherConfig.createView((props) => {
  const [fieldProps, controlProps] = useFormFieldProps(props);

  return (
    <FormField {...fieldProps}>
      <NativeCodePuncher
        {...controlProps}
        subviews={props.subviews}
        error={!!fieldProps.error}
        inherits={props.subviews.input}
      />
    </FormField>
  );
});
